import { FAMILY } from '@app/shared'
import type { InlineRun, NodeContent } from './content-model'
import { nodeTextPartsOf } from './content-text'
import type { BookTypeface } from './language'

/**
 * What each face a book sets draws, as `bun run fonts:coverage` read it off the
 * vendored files: the characters every cut maps, as inclusive runs, and those
 * any cut maps to a glyph that draws nothing.
 */
export type FaceCoverage = {
  families: Record<string, { maps: number[][]; empty: number[] }>
}

/**
 * The faces one kind of text is set in, in the order the stylesheet names them.
 *
 * The same stacks `book.css` declares, and they have to be: a character the
 * first face does not map falls through to the next, while one it maps to an
 * empty glyph stops there and prints a gap. A stack ending in a generic is
 * not walked past our faces — what a reader's machine holds is no answer.
 */
export const BOOK_STACKS = {
  /** The running text in the sans, which is every book that chose nothing. */
  sans: [FAMILY.sans],
  /** The running text in the serif, which carries both scripts alone. */
  serif: [FAMILY.serif],
  /** A standalone Hebrew line: a verse or a source quote. */
  display: [FAMILY.book, FAMILY.sans],
  /** The book's own title on its page. */
  title: [FAMILY.serif, FAMILY.sans],
} as const

export type UnsetCharacter = {
  /** The family that stopped the character, or the last one asked. */
  face: string
  codepoint: number
  /** Mapped to a glyph that draws nothing, or mapped by no face in the stack. */
  reason: 'empty' | 'unmapped'
}

/* Characters that draw nothing on purpose — spaces, separators, controls and
   format characters — the same categories the coverage sweep leaves out. */
const BLANK = /[\p{Zs}\p{Zl}\p{Zp}\p{Cc}\p{Cf}\p{Co}]/u

const maps = (runs: number[][], cp: number): boolean => {
  let lo = 0
  let hi = runs.length - 1
  while (lo <= hi) {
    const mid = (lo + hi) >> 1
    const [from, to] = runs[mid]
    if (cp < from) hi = mid - 1
    else if (cp > to) lo = mid + 1
    else return true
  }
  return false
}

/** Each character of `text` the stack cannot set, once per codepoint. */
export function unsetIn(
  text: string,
  stack: readonly string[],
  coverage: FaceCoverage,
): UnsetCharacter[] {
  const found = new Map<number, UnsetCharacter>()
  for (const ch of text) {
    const cp = ch.codePointAt(0) as number
    if (found.has(cp) || BLANK.test(ch)) continue
    let answer: UnsetCharacter | null = {
      face: stack[stack.length - 1],
      codepoint: cp,
      reason: 'unmapped',
    }
    for (const face of stack) {
      const held = coverage.families[face]
      if (!held || !maps(held.maps, cp)) continue
      answer = held.empty.includes(cp)
        ? { face, codepoint: cp, reason: 'empty' }
        : null
      break
    }
    if (answer) found.set(cp, answer)
  }
  return [...found.values()]
}

const words = (run: InlineRun): string =>
  run.map((n) => (n.t === 'text' ? n.v : '')).join('')

/**
 * What one node sets that its faces cannot, read part by part — a passage's
 * words or a part's heading.
 *
 * A verse's and a source quote's Hebrew are the display line; everything
 * else is the running text in the book's own face.
 */
export function unsetInNode(
  content: NodeContent,
  face: BookTypeface,
  coverage: FaceCoverage,
): UnsetCharacter[] {
  const display = content.kind === 'verse' || content.kind === 'source_quote'
  const found = new Map<number, UnsetCharacter>()
  for (const part of nodeTextPartsOf(content)) {
    for (const seg of part.segments) {
      if (seg.of === 'joiner') continue
      const text = seg.of === 'run' ? words(seg.run) : seg.value
      const stack =
        display && seg.of === 'run' && seg.path === 'he'
          ? BOOK_STACKS.display
          : BOOK_STACKS[face]
      for (const unset of unsetIn(text, stack, coverage)) {
        if (!found.has(unset.codepoint)) found.set(unset.codepoint, unset)
      }
    }
  }
  return [...found.values()]
}
